import React, { useContext, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { FiArrowRight, FiMinus, FiPlus, FiTrash2 } from "react-icons/fi";
import { CartContext } from "./CartContext";
import productData from "../Datas/productData";
import siteConfig, { formatPrice } from "../config/siteConfig";
import "../css/cartpage.scss";

const CartPage = () => {
  const navigate = useNavigate();
  const { cartItems, removeFromCart, updateQuantity, clearCart } = useContext(CartContext);
  const [promoCode, setPromoCode] = useState("");
  const [promoApplied, setPromoApplied] = useState(false);
  const [promoMessage, setPromoMessage] = useState("");

  const detailedItems = useMemo(
    () =>
      cartItems
        .map((item) => ({
          ...item,
          product: productData.find((product) => product.id === item.id),
        }))
        .filter((item) => item.product),
    [cartItems]
  );

  const subtotal = detailedItems.reduce(
    (total, item) => total + item.product.price * item.quantity,
    0
  );
  const discount = promoApplied ? subtotal * 0.1 : 0;
  const shipping =
    subtotal - discount >= siteConfig.commerce.freeShippingThreshold
      ? 0
      : siteConfig.commerce.standardShipping;
  const total = subtotal - discount + shipping;
  const amountToFreeShipping = Math.max(
    0,
    siteConfig.commerce.freeShippingThreshold - (subtotal - discount)
  );

  const toTop = () => window.scrollTo({ top: 0, behavior: "smooth" });

  const applyPromo = (event) => {
    event.preventDefault();
    const isValid = promoCode.trim().toUpperCase() === "WELCOME10";
    setPromoApplied(isValid);
    setPromoMessage(
      isValid
        ? "WELCOME10 has been applied."
        : "This code is not available for the current edit."
    );
  };

  if (!detailedItems.length) {
    return (
      <main className="cart-page cart-page-empty">
        <p className="eyebrow">Your bag</p>
        <h1>Cart</h1>
        <p>Your cart is empty.</p>
        <Link to="/shop" onClick={toTop} className="solid-button">
          Continue shopping
        </Link>
      </main>
    );
  }

  return (
    <main className="cart-page">
      <header className="cart-header">
        <p className="eyebrow">Your selection</p>
        <h1>Shopping bag</h1>
        <span>{detailedItems.length} {detailedItems.length === 1 ? "piece" : "pieces"}</span>
      </header>

      <div className="cart-layout">
        <section className="cart-items" aria-label="Shopping bag items">
          {amountToFreeShipping > 0 ? (
            <div className="shipping-progress">
              <p>
                Add {formatPrice(amountToFreeShipping)} for complimentary delivery.
              </p>
              <span>
                <i
                  role="progressbar"
                  aria-label="Progress toward complimentary delivery"
                  aria-valuemin="0"
                  aria-valuemax="100"
                  aria-valuenow={Math.round(
                    Math.min(
                      100,
                      ((subtotal - discount) /
                        siteConfig.commerce.freeShippingThreshold) *
                        100
                    )
                  )}
                  style={{
                    width: `${Math.min(
                      100,
                      ((subtotal - discount) /
                        siteConfig.commerce.freeShippingThreshold) *
                        100
                    )}%`,
                  }}
                />
              </span>
            </div>
          ) : (
            <p className="shipping-earned">Complimentary delivery unlocked.</p>
          )}

          {detailedItems.map((item) => (
            <article
              className="cart-item"
              key={`${item.id}-${item.size}-${item.color}`}
            >
              <Link className="cart-item-image" to={`/shopDetails/${item.id}`} onClick={toTop}>
                <img src={item.product.thumbnail} alt={item.product.name} />
              </Link>
              <div className="cart-item-copy">
                <div>
                  <Link to={`/shopDetails/${item.id}`} onClick={toTop}>
                    {item.product.name}
                  </Link>
                  <p>{item.color} / {item.size}</p>
                </div>
                <button
                  type="button"
                  className="cart-remove"
                  onClick={() => removeFromCart(item.id, item.size, item.color)}
                >
                  <FiTrash2 aria-hidden="true" /> Remove
                </button>
              </div>
              <div className="cart-item-controls">
                <div
                  className="cart-quantity"
                  role="group"
                  aria-label={`Quantity for ${item.product.name}`}
                >
                  <button
                    type="button"
                    onClick={() =>
                      updateQuantity(
                        item.id,
                        item.size,
                        item.color,
                        Math.max(1, item.quantity - 1)
                      )
                    }
                    aria-label="Decrease quantity"
                  >
                    <FiMinus aria-hidden="true" />
                  </button>
                  <span>{item.quantity}</span>
                  <button
                    type="button"
                    onClick={() =>
                      updateQuantity(
                        item.id,
                        item.size,
                        item.color,
                        item.quantity + 1
                      )
                    }
                    aria-label="Increase quantity"
                  >
                    <FiPlus aria-hidden="true" />
                  </button>
                </div>
                <strong>{formatPrice(item.product.price * item.quantity)}</strong>
              </div>
            </article>
          ))}

          <div className="cart-list-footer">
            <Link to="/shop" onClick={toTop} className="text-link align-left">
              Continue shopping
            </Link>
            <button type="button" onClick={clearCart}>Clear bag</button>
          </div>
        </section>

        <aside className="cart-summary">
          <h2>Order summary</h2>
          <form className="promo-form" onSubmit={applyPromo}>
            <label htmlFor="promo-code">Promo code</label>
            <div>
              <input
                id="promo-code"
                value={promoCode}
                onChange={(event) => {
                  setPromoCode(event.target.value);
                  setPromoApplied(false);
                  setPromoMessage("");
                }}
                placeholder="Enter code"
              />
              <button type="submit">Apply</button>
            </div>
            {promoMessage && <p role="status">{promoMessage}</p>}
          </form>

          <dl className="summary-lines">
            <div>
              <dt>Subtotal</dt>
              <dd>{formatPrice(subtotal)}</dd>
            </div>
            {discount > 0 && (
              <div>
                <dt>Discount</dt>
                <dd>−{formatPrice(discount)}</dd>
              </div>
            )}
            <div>
              <dt>Delivery</dt>
              <dd>{shipping ? formatPrice(shipping) : "Complimentary"}</dd>
            </div>
            <div className="summary-total">
              <dt>Total <small>VAT included</small></dt>
              <dd>{formatPrice(total)}</dd>
            </div>
          </dl>

          <button
            type="button"
            className="checkout-button"
            onClick={() => {
              navigate("/checkout", {
                state: { promoCode: promoApplied ? "WELCOME10" : "" },
              });
              toTop();
            }}
          >
            Checkout <FiArrowRight aria-hidden="true" />
          </button>
          <p className="secure-note">Secure checkout · Taxes included · Tracked delivery</p>
        </aside>
      </div>
    </main>
  );
};

export default CartPage;
